// SPDX-License-Identifier: GPL-3.0-only
// Character-format semantics derived from OpenSaga (opensagadev/saga),
// src/legoapi/characters/core/charconfig.cpp. Original game assets stay local.

const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;

function valueOf(raw) {
  if (raw.startsWith('"') && raw.endsWith('"')) return raw.slice(1, -1);
  if (raw.includes(',')) return raw.split(',').map(item => valueOf(item.trim()));
  if (NUMBER.test(raw)) {
    const value = Number(raw);
    if (!Number.isFinite(value)) throw new Error('Character configuration contains a non-finite number.');
    return value;
  }
  if (raw === 'on') return true;
  if (raw === 'off') return false;
  return raw;
}

export function parseCharacterConfig(text) {
  if (typeof text !== 'string' || text.length > 4 * 1024 * 1024) throw new Error('Invalid character configuration text.');
  const values = Object.create(null), layers = Object.create(null);
  const animations = new Map(), actions = new Map();
  let animation = null, bsaDefault = false;
  for (const original of text.replace(/^\uFEFF/, '').split(/\r?\n/)) {
    // Comments are stripped only outside quoted asset names.
    let line = '', quoted = false;
    for (let i = 0; i < original.length; i++) {
      if (original[i] === '"') quoted = !quoted;
      if (!quoted && original[i] === '/' && original[i + 1] === '/') break;
      line += original[i];
    }
    line = line.trim();
    if (!line) continue;
    const match = /^([a-z_][a-z0-9_]*)(?:\s*=\s*|\s+)?(.*)$/i.exec(line);
    if (!match) throw new Error(`Invalid character configuration line: ${line.slice(0, 80)}`);
    const key = match[1].toLowerCase(), raw = match[2].trim();
    const value = raw ? valueOf(raw) : true;
    if (key === 'anim_start') {
      if (animation || typeof value !== 'string' || !value || animations.size >= 100) throw new Error('Invalid animation block.');
      animation = { clip: value.toLowerCase(), action: null, playbackRate: 30,
        blendIn: 0.2, blendOut: 0.2, actionSpeed: 0, eventFrames: [0, 0, 0, 0],
        cycle: true, bsa: bsaDefault, stopFrame: 0, flags: Object.create(null) };
      continue;
    }
    if (key === 'anim_end') {
      if (!animation) throw new Error('Animation end without animation start.');
      animations.set(animation.clip, animation);
      // Source action_id starts at -1: an omitted action never invents one.
      if (animation.action !== null) actions.set(animation.action, animation);
      animation = null;
      continue;
    }
    if (animation) {
      if (key === 'action') {
        if (typeof value !== 'string' || !value) throw new Error('Invalid animation action.');
        animation.action = value.toLowerCase();
      } else if (['fpsec', 'blend_in', 'blend_out', 'speed', 'frame', 'frame_hit', 'frame1', 'frame2', 'frame_end', 'frame3', 'frame4', 'frame_stop'].includes(key)) {
        if (typeof value !== 'number' || value < 0 || (key === 'fpsec' && value === 0)) throw new Error(`Invalid animation ${key}.`);
        if (key === 'fpsec') animation.playbackRate = value;
        else if (key === 'blend_in') animation.blendIn = value;
        else if (key === 'blend_out') animation.blendOut = value;
        else if (key === 'speed') animation.actionSpeed = value;
        else if (key === 'frame_stop') animation.stopFrame = value;
        else animation.eventFrames[{ frame: 0, frame_hit: 0, frame1: 0, frame2: 1, frame_end: 1, frame3: 2, frame4: 3 }[key]] = value;
      } else if (key === 'cycle' || key === 'bsa') {
        if (typeof value !== 'boolean') throw new Error(`Invalid animation ${key}.`);
        animation[key] = value;
      } else animation.flags[key] = value;
    } else {
      values[key] = value;
      if (key === 'bsa_default') bsaDefault = value === true;
      if (key.startsWith('layers_')) {
        const indices = Array.isArray(value) ? value : [value];
        if (indices.some(index => !Number.isInteger(index) || index < 0 || index > 31)) throw new Error('Invalid character layer index.');
        layers[key.slice(7)] = indices;
      }
    }
  }
  if (animation) throw new Error('Unterminated animation block.');
  return { values, layers, actions, animations };
}
